import { useEffect, useState } from 'react';
import { Brain, Save, Loader2 } from 'lucide-react';
import { getAiSettings, updateAiSettings } from '../../services/aiTutor';
import { useToast } from '../../components/ui/Modal';

export default function AISettings() {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const toast = useToast();

    useEffect(() => {
        getAiSettings()
            .then((res) => setSettings(res.data))
            .catch(() => toast.error('Sozlamalarni yuklashda xatolik'))
            .finally(() => setLoading(false));
    }, []);

    const handleChange = (field, value) => {
        setSettings((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await updateAiSettings({
                api_key: settings.api_key,
                model_name: settings.model_name,
                temperature: parseFloat(settings.temperature),
                system_prompt: settings.system_prompt,
                is_active: settings.is_active,
            });
            toast.success('AI sozlamalari saqlandi');
            // Mask key after save
            setSettings((prev) => ({ ...prev, api_key: '••••••••' }));
        } catch (err) {
            toast.error(err.response?.data?.message || 'Saqlashda xatolik');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
        );
    }

    return (
        <div className="max-w-3xl mx-auto space-y-6">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white">
                <h1 className="text-2xl font-bold flex items-center gap-2">
                    <Brain className="w-7 h-7" /> AI Sozlamalari
                </h1>
                <p className="text-blue-100 mt-1">AI-Tutor uchun OpenAI integratsiyasi sozlamalari</p>
            </div>

            <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700 space-y-5">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">OpenAI API Key</label>
                    <input
                        type="password"
                        value={settings.api_key || ''}
                        onChange={(e) => handleChange('api_key', e.target.value)}
                        placeholder="sk-..."
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                    <p className="text-xs text-gray-500 mt-1">Shifrlangan holda saqlanadi. O'zgartirmasangiz, avvalgi kalit saqlanib qoladi.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Model nomi</label>
                        <input
                            type="text"
                            value={settings.model_name || ''}
                            onChange={(e) => handleChange('model_name', e.target.value)}
                            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Temperature (0–2)</label>
                        <input
                            type="number"
                            min="0"
                            max="2"
                            step="0.1"
                            value={settings.temperature ?? 0.2}
                            onChange={(e) => handleChange('temperature', e.target.value)}
                            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">System Prompt</label>
                    <textarea
                        value={settings.system_prompt || ''}
                        onChange={(e) => handleChange('system_prompt', e.target.value)}
                        rows={10}
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 outline-none font-mono text-sm"
                    />
                </div>

                <div className="flex items-center gap-2">
                    <input
                        id="is_active"
                        type="checkbox"
                        checked={settings.is_active || false}
                        onChange={(e) => handleChange('is_active', e.target.checked)}
                        className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <label htmlFor="is_active" className="text-sm text-gray-700 dark:text-gray-300">AI-Tutor faol</label>
                </div>

                <div className="pt-2">
                    <button
                        type="submit"
                        disabled={saving}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-50 transition"
                    >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        {saving ? 'Saqlanmoqda...' : 'Saqlash'}
                    </button>
                </div>
            </form>
        </div>
    );
}
